import * as React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Switch,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { EnterpriseClassification, Group, Sector } from '@esg/esg-global-types';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import ConfigAddWidget, { CreateInput } from '../../configuration/ConfigAddWidget';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { DataGrid, GridActionsCellItem, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { ReactComponent as GroupIcon } from '../../../assets/icons/Group.svg';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { group_label } from '../../../lib/app/group';
import { useNavigate } from 'react-router-dom';
import SectorSelect from '../../shared/input/select/SectorSelect';
import EnterpriseClassificationSelect from '../../shared/input/select/EnterpriseClassificationSelect';
import GuidanceSelect from '../../shared/input/select/GuidanceSelect';
import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { createGroup, getGroups, updateGroup } from '../../../graphql/services/group.service';
import { CreateGroupClientData } from '@esg/syntiro-api-core';

/**
 * Datagrid handling the display of Groups
 * @returns {JSX.Element}
 */
const PanelGroups = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [actionRow, setActionRow] = React.useState<Group | null>(null);
  const [groupRows, setGroupRows] = React.useState<Array<Group>>([]);
  const [displayWidgetPanelAdd, setDisplayWidgetPanelAdd] = React.useState<boolean>(false);
  const [openDialogActive, setOpenDialogActive] = React.useState<boolean>(false);
  const [gridLoading, setGridLoading] = React.useState<boolean>(true);
  const [actionLoading, setActionLoading] = React.useState<boolean>(false);
  const theme: Theme = useTheme();

  const sectorsInputFactory = (
    handleInputChange: (input_id: string, value: Array<Sector> | Sector | null) => void
  ) => {
    return (
      <SectorSelect
        master_list
        input_label="Sectors"
        multi_select
        tag_limit={4}
        select_limit={4}
        handleChangeSectors={(sectors: Array<Sector> | Sector | null) => {
          handleInputChange('sectors', sectors);
        }}
      />
    );
  };

  const enterpriseClassificationInputFactory = (
    handleInputChange: (input_id: string, value: EnterpriseClassification | null) => void
  ) => {
    return (
      <EnterpriseClassificationSelect
        clearable={false}
        handleChangeEnterpriseClassification={(
          enterprise_classification: EnterpriseClassification | null
        ) => {
          handleInputChange('enterprise_classification', enterprise_classification);
        }}
      />
    );
  };

  const guidanceInputFactory = (handleInputChange: (input_id: string, value: boolean) => void) => {
    return (
      <GuidanceSelect
        handleGuidanceTypeChange={(value: boolean | null) => {
          handleInputChange('guided_readiness', !!value);
        }}
      />
    );
  };

  const navigate = useNavigate();

  const viewGroup = (group: Group) => {
    navigate('/settings/group-management/view', { state: group });
  };

  // Handler functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setDisplayWidgetPanelAdd(true);
  };

  const handleCloseActiveModal = () => {
    setOpenDialogActive(false);
  };

  const handleEditGroup = (updated_row: Group) => {
    const new_rows = groupRows.map((row) => (updated_row.id === row.id ? { ...updated_row } : row));
    setGroupRows(new_rows);
  };

  const handleGroupActive = async (new_active: boolean) => {
    try {
      if (actionRow) {
        setActionLoading(true);
        await updateGroup({}, { id: actionRow.id, active: new_active }).then(() => {
          handleEditGroup({ ...actionRow, active: new_active });
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelGroups failed on an unknown error while calling handleGroupActive.',
          {
            actionRow: actionRow,
            new_active: new_active
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      handleCloseActiveModal();
      setActionLoading(false);
    }
  };

  const columns: Array<GridColDef> = [
    {
      field: 'active',
      headerName: 'Active',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: (params) => {
        return (
          <Switch
            checked={params.row.active ? true : false}
            onChange={() => {
              setActionRow(params.row);
              setOpenDialogActive(true);
            }}
          />
        );
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'companies',
      headerName: 'Companies',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return Array.isArray(params.value) ? params.value.length : 0;
      },
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'left',
      align: 'left',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title={`View ${row.name}`}>
                  <ChevronRightOutlinedIcon sx={{ fontSize: '2.5rem' }} color="primary" />
                </Tooltip>
              }
              size="large"
              label="View"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => {
                viewGroup(row);
              }}
              disabled={!row.active}
            />
          </>
        ];
      }
    }
  ];

  const inputs: readonly CreateInput[] = [
    {
      id: 'name',
      type: 'text',
      label: 'Name'
    },
    {
      id: 'sectors',
      type: 'node',
      label: 'Sectors',
      inputNodeFactory: sectorsInputFactory
    },
    {
      id: 'enterprise_classification',
      type: 'node',
      label: 'Enterprise Classification',
      inputNodeFactory: enterpriseClassificationInputFactory
    },
    {
      id: 'guided_readiness',
      type: 'node',
      label: 'Guided Readiness',
      inputNodeFactory: guidanceInputFactory
    }
  ];

  // Row functions
  const fetchRows = async () => {
    setGridLoading(true);
    try {
      // Load rows from database.
      const groups: Array<Group> = await getGroups();
      // Load rows to memory.
      setGroupRows(groups);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelGroups failed on an unknown error while calling fetchRows.',
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch groups. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      (async () => {
        setGroupRows([]);
        fetchRows();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelGroups failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, []);

  const createRow = async (
    name: string,
    sectors: Array<Sector>,
    enterprise_classification: EnterpriseClassification,
    guided_readiness: boolean
  ): Promise<void> => {
    try {
      // Create row in database.
      const create_group_client_data: CreateGroupClientData = {
        deleted: null,
        active: true,
        name,
        enterprise_classification,
        guided_readiness,
        sectors
      };
      const group: Group | null | undefined = await createGroup({}, create_group_client_data, {
        create_audit_log: true,
        return_data: true
      });
      if (group) {
        // Create grid row in memory
        setGroupRows([
          {
            id: group.id,
            deleted: null,
            name,
            active: true,
            sectors,
            enterprise_classification,
            guided_readiness,
            companies: []
          },
          ...groupRows
        ]);
      } else {
        throw new MetadataError('Could not create Group in database', {
          name,
          sectors,
          enterprise_classification,
          guided_readiness
        });
      }
      // Close side menu.
      setDisplayWidgetPanelAdd(false);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelGroups failed on an unknown error while calling createRow.',
          {
            name
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create Group. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  return (
    <>
      {/* Set Group Active Modal */}
      <Dialog open={openDialogActive} onClose={handleCloseActiveModal}>
        <Box sx={{ p: 1.5 }}>
          <DialogTitle id="alert-dialog-title">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <WarningAmberIcon sx={{ marginRight: '1rem' }} fontSize="medium" />
              <Typography variant="h6" color="inherit" align="left">
                {actionRow?.active ? 'Disable' : 'Enable'}{' '}
                {actionRow?.name ? actionRow.name : 'Group'}?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              You are about to {actionRow?.active ? 'deactivate' : 'activate'}{' '}
              {actionRow?.name ? actionRow.name : 'group'} {actionRow?.active ? 'from' : 'for'} your
              Groups.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <GhostButton onClick={handleCloseActiveModal} outlined dark>
              Back
            </GhostButton>
            <PrimaryButton
              loading={actionLoading}
              onClick={() => actionRow && handleGroupActive(actionRow.active ? false : true)}
              palette_color="error"
              autoFocus
            >
              Confirm
            </PrimaryButton>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelAdd}
        onClose={() => setDisplayWidgetPanelAdd(false)}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        <ConfigAddWidget
          widget_label={group_label.one}
          icon={<GroupIcon height={40} stroke={theme.palette.primary.main} />}
          inputs={inputs}
          grid_spacing={3}
          handleClose={() => setDisplayWidgetPanelAdd(false)}
          createFunction={(
            name: string,
            sectors: Array<Sector>,
            enterprise_classification: EnterpriseClassification,
            guided_readiness: boolean
          ) => createRow(name, sectors, enterprise_classification, guided_readiness)}
        />
      </Drawer>

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        rowHeight={65}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'modified', sort: 'desc' }]
          }
        }}
        hideFooter={groupRows.length > 1 ? false : true}
        columnHeaderHeight={groupRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={groupRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar entity_label={group_label} handleCreate={handleCreateClick} />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={group_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
      />
    </>
  );
};

export default PanelGroups;
